import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { Field } from 'formik';
import { api } from '../services/api';

interface SelectSearchProps {
  name: string;
  placeholder?: string;
  error?: string;
  onChange?: (value: string) => void;
  value?: string; // ID do cliente pré-selecionado
}

interface Customer {
  id: string;
  name: string;
}

export function SelectSearch({
  name,
  placeholder = 'Digite para buscar...',
  error,
  onChange,
  value: initialValue,
}: SelectSearchProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null,
  );
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const lastLoadedValueRef = useRef<string | undefined>(undefined);

  const searchCustomers = useCallback(async (term = '', openDropdown = true) => {
    setLoading(true);
    try {
      const response = await api.get(`/costumers/search?name=${term}`);
      setCustomers(response.data);
      if (openDropdown) {
        setShowDropdown(true);
      }
    } catch (err) {
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load all customers on component mount (without opening dropdown)
  useEffect(() => {
    searchCustomers('', false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carregar cliente pré-selecionado quando value é passado
  useEffect(() => {
    // Só carregar se o valor mudou desde a última vez
    if (initialValue && initialValue !== lastLoadedValueRef.current) {
      const loadSelectedCustomer = async () => {
        try {
          const response = await api.get(`/costumers/${initialValue}`);
          if (response.data) {
            setSelectedCustomer(response.data);
            setSearchTerm(response.data.name);
            lastLoadedValueRef.current = initialValue;
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Erro ao carregar cliente:', err);
        }
      };
      loadSelectedCustomer();
    }
  }, [initialValue]);

  // Debounce search - only when user is typing (dropdown already open)
  useEffect(() => {
    // Skip if dropdown is not open (user hasn't focused the input)
    if (!showDropdown) return undefined;

    const timer = setTimeout(() => {
      searchCustomers(searchTerm, false);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, searchCustomers, showDropdown]);

  // Handle clicks outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current
        && !dropdownRef.current.contains(event.target as Node)
        && inputRef.current
        && !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value: inputValue } = e.target;
    setSearchTerm(inputValue);

    // Só limpa o valor se o usuário está ativamente editando (não apenas um re-render)
    if (!inputValue && selectedCustomer) {
      setSelectedCustomer(null);
      onChange?.('');
    }
  };

  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    setSearchTerm(customer.name);
    setShowDropdown(false);
    // Usar um pequeno delay para garantir que o estado foi atualizado antes de notificar
    setTimeout(() => {
      onChange?.(customer.id);
    }, 0);
  };

  const inputClassName = error
    ? 'h-[52px] px-4 w-full rounded-lg text-red-500 border-2 border-red-500 bg-white focus:outline-none focus:ring-0'
    : 'h-[52px] px-4 w-full rounded-lg text-[#7FA5B2] bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD]';

  return (
    <div className="relative w-full">
      <Field name={name}>
        {({ form }) => (
          <>
            <input
              ref={inputRef}
              type="text"
              placeholder={placeholder}
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={() => {
                setShowDropdown(true);
                if (customers.length === 0 && !loading) {
                  searchCustomers(searchTerm, false);
                }
              }}
              className={inputClassName}
              autoComplete="off"
            />

            {loading && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin h-4 w-4 border-2 border-[#0085BD] border-t-transparent rounded-full" />
              </div>
            )}

            {showDropdown && customers.length > 0 && (
              <div
                ref={dropdownRef}
                className="absolute z-[60] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
              >
                {customers.map((customer) => (
                  <button
                    type="button"
                    key={customer.id}
                    onClick={() => handleCustomerSelect(customer)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 cursor-pointer text-[#7FA5B2] border-b border-gray-100 last:border-b-0"
                  >
                    {customer.name}
                  </button>
                ))}
              </div>
            )}

            {showDropdown
              && searchTerm.length > 0
              && customers.length === 0
              && !loading && (
              <div
                ref={dropdownRef}
                className="absolute z-[60] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg"
              >
                <div className="px-4 py-3 text-gray-500 text-center">
                  Nenhum cliente encontrado
                </div>
              </div>
            )}
          </>
        )}
      </Field>

      {error && <div className="text-xs text-red-500 mt-1">{error}</div>}
    </div>
  );
}
